/**
 * HistoryPage.jsx - Scriptus Pharma Partner
 * Histórico de dispensações com design glassmorphism premium
 * PADRONIZADO COM ASSINATURA.JSX - FONTES E TAMANHOS UNIFICADOS
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  FileText, 
  Calendar, 
  Package,
  ChevronDown,
  Loader2,
  Search,
  Filter,
  Clock,
  CheckCircle,
  TrendingUp,
  BarChart3,
  X,
  Menu,
  Info,
  AlertCircle,
  RefreshCw,
  Eye,
  QrCode
} from 'lucide-react';
import HeaderMenu from '../components/HeaderMenu';
import ModalVisualizarDispensacao from '../components/ModalVisualizarDispensacao';
import { auth } from '../config/firebase';

// ============================================
// API Service
// ============================================
const API_BASE = import.meta.env.VITE_API_URL || 'https://api.scriptusrxia.com.br';

async function getAuthToken() {
  try {
    const user = auth.currentUser;
    if (user) {
      return await user.getIdToken();
    }
    return null;
  } catch (error) {
    console.error('Erro ao obter token:', error);
    return null;
  }
}

async function apiRequest(endpoint, options = {}) {
  const token = await getAuthToken();
  
  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers
    }
  };

  const response = await fetch(`${API_BASE}${endpoint}`, config);
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error?.message || `Erro ${response.status}`);
  }
  
  return response.json();
}

// ============================================
// COMPONENTES AUXILIARES
// ============================================

function GlassCard({ children, className = '', elevated = false }) {
  return (
    <div className={`${elevated ? 'glass-card-elevated-refined' : 'glass-card-refined'} rounded-2xl ${className}`}>
      {children}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color = 'emerald' }) {
  const colorClasses = {
    emerald: {
      bg: 'bg-emerald-100/80',
      border: 'border-emerald-200/50',
      icon: 'text-emerald-600',
    },
    blue: {
      bg: 'bg-blue-100/80',
      border: 'border-blue-200/50',
      icon: 'text-blue-600',
    },
    purple: {
      bg: 'bg-purple-100/80',
      border: 'border-purple-200/50',
      icon: 'text-purple-600',
    }
  };

  const colors = colorClasses[color] || colorClasses.emerald;

  return (
    <GlassCard elevated className="p-4 hover:shadow-xl transition-all duration-200 group">
      <div className="flex items-center justify-between mb-3">
        <div className={`icon-container-refined ${colors.bg} ${colors.border} p-2.5 rounded-xl group-hover:scale-105 transition-transform duration-200`}>
          <Icon className={`w-4 h-4 ${colors.icon}`} />
        </div>
      </div>
      <p className="text-xl sm:text-2xl sf-pro-display font-bold text-gray-900 mb-0.5">{value}</p>
      <p className="sf-pro-text text-gray-500 text-xs font-medium">{label}</p>
    </GlassCard>
  );
}

function DispensationCard({ dispensation, onClick }) {
  const date = dispensation.dispensedAt?.toDate?.() || new Date(dispensation.dispensedAt);
  
  return (
    <div 
      className="glass-info-card-refined p-4 rounded-xl hover:shadow-lg transition-all duration-200 cursor-pointer group"
      onClick={onClick}
    >
      <div className="flex flex-col sm:flex-row items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1">
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-500/25 group-hover:scale-105 transition-transform duration-200">
            <FileText className="w-5 h-5 text-white" />
          </div>
          
          <div className="flex-1">
            <h3 className="sf-pro-display text-gray-900 font-semibold text-sm mb-1.5 group-hover:text-emerald-600 transition-colors">
              {dispensation.patientName || 'Paciente'}
            </h3>
            
            <div className="flex flex-wrap items-center gap-2.5 text-xs text-gray-600 mb-2">
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                <span className="sf-pro-text">
                  {date.toLocaleDateString('pt-BR', { 
                    day: '2-digit', 
                    month: 'short',
                    year: 'numeric'
                  })}
                </span>
              </span>
              
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                <span className="sf-pro-text">
                  {date.toLocaleTimeString('pt-BR', { 
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </span>
              
              <span className="flex items-center gap-1">
                <Package className="w-3.5 h-3.5" />
                <span className="sf-pro-text">
                  {dispensation.itemsCount || dispensation.items?.length || 0} {dispensation.itemsCount === 1 ? 'item' : 'itens'}
                </span>
              </span>
            </div>

            {dispensation.prescriptionId && (
              <div className="flex items-center gap-1.5">
                <span className="glass-badge-refined text-[10px] px-2 py-1">
                  <span className="sf-pro-text text-gray-600">ID: {dispensation.prescriptionId.substring(0, 8)}...</span>
                </span>
                <span className="glass-badge-refined text-[10px] px-2 py-1 bg-emerald-100/80 text-emerald-700 border-emerald-200/50">
                  <CheckCircle className="w-2.5 h-2.5 inline mr-0.5" />
                  <span className="sf-pro-text font-semibold">Dispensado</span>
                </span>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
            className="button-glass-refined text-gray-700 flex-1 sm:flex-initial"
          >
            <Eye className="w-3.5 h-3.5" />
            <span className="sf-pro-text text-xs">Ver Detalhes</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  const navigate = useNavigate();
  
  return (
    <GlassCard elevated className="p-10 text-center">
      <div className="relative inline-block mb-5">
        <div className="absolute inset-0 bg-gray-400/20 rounded-2xl blur-xl"></div>
        <div className="relative w-16 h-16 bg-gradient-to-br from-gray-100/80 to-gray-200/80 rounded-2xl flex items-center justify-center shadow-inner">
          <FileText className="w-8 h-8 text-gray-400" />
        </div>
      </div>
      <h3 className="sf-pro-display font-bold text-gray-900 text-base mb-2">
        Nenhuma dispensação encontrada
      </h3>
      <p className="sf-pro-text text-gray-600 text-xs mb-6 max-w-md mx-auto">
        Suas dispensações aparecerão aqui após você processar sua primeira receita digital.
      </p>
      <button
        onClick={() => navigate('/dashboard')}
        className="button-primary-refined inline-flex items-center gap-2"
      >
        <QrCode className="w-4 h-4" />
        <span className="sf-pro-text font-semibold">Nova Dispensação</span>
      </button>
    </GlassCard>
  );
}

function ErrorState({ error, onRetry }) {
  return (
    <GlassCard elevated className="p-7 text-center">
      <div className="relative inline-block mb-4">
        <div className="absolute inset-0 bg-red-400/20 rounded-2xl blur-xl"></div>
        <div className="relative w-14 h-14 bg-red-100/80 rounded-2xl flex items-center justify-center border border-red-200/50">
          <AlertCircle className="w-7 h-7 text-red-500" />
        </div>
      </div>
      <h3 className="sf-pro-display font-bold text-gray-900 text-base mb-1.5">
        Erro ao carregar histórico
      </h3>
      <p className="sf-pro-text text-gray-600 text-xs mb-5">
        {error?.message || 'Ocorreu um erro inesperado'}
      </p>
      <button
        onClick={onRetry}
        className="button-glass-refined text-gray-700"
      >
        <RefreshCw className="w-3.5 h-3.5" />
        <span className="sf-pro-text text-xs">Tentar Novamente</span>
      </button>
    </GlassCard>
  );
}

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

export default function HistoryPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [sidebarMobile, setSidebarMobile] = useState(false);
  
  // Estados de dados
  const [dispensacoes, setDispensacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastCursor, setLastCursor] = useState(null);

  // Estados do modal
  const [modalAberto, setModalAberto] = useState(false);
  const [dispensacaoSelecionada, setDispensacaoSelecionada] = useState(null);

  // Carregar histórico inicial
  const loadHistorico = useCallback(async (refresh = false) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiRequest('/api/v1/dispensation/history?limit=20');
      
      if (response.success) {
        const items = response.dispensations || [];
        setDispensacoes(items);
        setHasMore(items.length >= 20);
        
        if (items.length > 0) {
          const lastItem = items[items.length - 1];
          setLastCursor(lastItem.dispensedAt);
        }
      }
    } catch (err) {
      console.error('Erro ao carregar histórico:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Carregar mais dispensações
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !lastCursor) return;

    try {
      setLoadingMore(true);

      const response = await apiRequest(
        `/api/v1/dispensation/history?limit=20&cursor=${encodeURIComponent(lastCursor)}`
      );
      
      if (response.success) {
        const novasDispensacoes = response.dispensations || [];
        
        if (novasDispensacoes.length === 0) {
          setHasMore(false);
        } else {
          setDispensacoes(prev => [...prev, ...novasDispensacoes]);
          setHasMore(novasDispensacoes.length >= 20);
          
          const lastItem = novasDispensacoes[novasDispensacoes.length - 1];
          setLastCursor(lastItem.dispensedAt);
        }
      }
    } catch (err) {
      console.error('Erro ao carregar mais:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, lastCursor]);

  // Download XML
  const handleDownloadXml = useCallback(async (prescriptionId) => {
    if (!prescriptionId) return;
    
    try {
      const response = await apiRequest(`/api/v1/dispensation/xml/${encodeURIComponent(prescriptionId)}`);
      
      if (response.success && response.xml) {
        const blob = new Blob([response.xml], { type: 'application/xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `SNGPC_${prescriptionId}_${Date.now()}.xml`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        throw new Error('XML não disponível');
      }
    } catch (error) {
      console.error('Erro ao baixar XML:', error);
      alert('Erro ao baixar XML. Tente novamente.');
    }
  }, []);

  // Abrir modal com dispensação selecionada
  const abrirModal = useCallback((dispensacao) => {
    setDispensacaoSelecionada(dispensacao);
    setModalAberto(true);
  }, []);

  // Fechar modal
  const fecharModal = useCallback(() => {
    setModalAberto(false);
    setDispensacaoSelecionada(null);
  }, []);

  // Effect de scroll otimizado
  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const isScrolled = window.scrollY > 10;
          if (isScrolled !== scrolled) {
            setScrolled(isScrolled);
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  // Carregar dados ao montar
  useEffect(() => {
    loadHistorico();
  }, [loadHistorico]);

  // Filtro por busca
  const filteredDispensations = dispensacoes.filter(d => 
    !searchTerm || 
    d.patientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.prescriptionId?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Agrupar por data
  const groupedDispensations = () => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    
    const ontem = new Date(hoje);
    ontem.setDate(ontem.getDate() - 1);
    
    const inicioSemana = new Date(hoje);
    inicioSemana.setDate(hoje.getDate() - hoje.getDay());
    
    const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);

    const grupos = {
      "Hoje": [],
      "Ontem": [],
      "Esta semana": [],
      "Este mês": [],
      "Mais antigos": []
    };

    filteredDispensations.forEach(disp => {
      const dataDisp = disp.dispensedAt?.toDate?.() || new Date(disp.dispensedAt);
      dataDisp.setHours(0, 0, 0, 0);

      if (dataDisp.getTime() === hoje.getTime()) {
        grupos["Hoje"].push(disp);
      } else if (dataDisp.getTime() === ontem.getTime()) {
        grupos["Ontem"].push(disp);
      } else if (dataDisp >= inicioSemana) {
        grupos["Esta semana"].push(disp);
      } else if (dataDisp >= inicioMes) {
        grupos["Este mês"].push(disp);
      } else {
        grupos["Mais antigos"].push(disp);
      }
    });

    return Object.entries(grupos).filter(([_, items]) => items.length > 0);
  };

  const grupos = groupedDispensations();

  // Calcular stats
  const statsThisMonth = filteredDispensations.filter(d => {
    const date = d.dispensedAt?.toDate?.() || new Date(d.dispensedAt);
    const inicioMes = new Date();
    inicioMes.setDate(1);
    inicioMes.setHours(0, 0, 0, 0);
    return date >= inicioMes;
  }).length;

  return (
    <div className={`min-h-screen bg-gradient-refined relative ${
      sidebarMobile ? 'overflow-hidden' : 'overflow-auto'
    }`}>
      
      {/* Background Pattern Premium */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="background-pattern-refined"></div>
        <div className="background-glow-1"></div>
        <div className="background-glow-2"></div>
        <div className="background-glow-3"></div>
      </div>

      {/* Overlay para sidebar mobile */}
      {sidebarMobile && (
        <div 
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setSidebarMobile(false)}
        />
      )}

      {/* Header Fixo Premium */}
      <header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled ? 'header-scrolled-refined' : 'header-refined'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarMobile(!sidebarMobile)}
              className="lg:hidden w-8 h-8 bg-white/70 hover:bg-white/90 rounded-lg border border-white/30 flex items-center justify-center shadow-sm hover:shadow-md transition-all duration-200 transform hover:scale-105"
            >
              <Menu className="w-3.5 h-3.5 text-gray-600" />
            </button>

            <button
              onClick={() => navigate('/dashboard')}
              className="logo-link-refined"
            >
              <img 
                src="/logo.PNG"
                alt="Scriptus Pharma Partner" 
                className="h-5 w-auto"
              />
            </button>
            
            <HeaderMenu />
          </div>
        </div>
      </header>

      {/* Sidebar Desktop */}
      <div className="hidden lg:block fixed top-0 left-0 bottom-0 w-72 z-20">
        <div className="h-full glass-card-elevated-refined border-r border-gray-200/50">
          <div className="p-5 h-full overflow-y-auto custom-scrollbar pt-20">
            
            <div className="space-y-2 mb-5">
              <button
                onClick={() => navigate('/dashboard')}
                className="sidebar-button-refined group"
              >
                <div className="sidebar-icon-refined">
                  <ArrowLeft className="w-3.5 h-3.5" />
                </div>
                <span className="sf-pro-text text-xs">Voltar ao Dispensador</span>
              </button>

              <button
                className="sidebar-button-refined sidebar-button-active-refined"
              >
                <div className="sidebar-icon-refined sidebar-icon-active-refined">
                  <Clock className="w-3.5 h-3.5" />
                </div>
                <span className="sf-pro-text text-xs">Histórico</span>
              </button>
            </div>

            <div className="glass-info-card-refined rounded-xl p-3.5 border border-gray-200/50">
              <h3 className="sf-pro-display font-bold text-gray-900 text-xs flex items-center gap-2 mb-2">
                <div className="icon-container-refined p-1 rounded-lg bg-emerald-100/80 border-emerald-200/50">
                  <Info className="w-3 h-3 text-emerald-600" />
                </div>
                Sobre o Histórico
              </h3>
              <p className="sf-pro-text text-[10px] text-gray-600 leading-relaxed mb-2.5">
                Visualize todas as dispensações realizadas, com detalhes completos de cada transação.
              </p>
              <div className="space-y-1.5 text-[10px] sf-pro-text text-gray-600">
                <div className="flex items-center gap-1.5">
                  <CheckCircle className="w-2.5 h-2.5 text-emerald-600" />
                  <span>Validação ICP-Brasil</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle className="w-2.5 h-2.5 text-emerald-600" />
                  <span>XML SNGPC gerado</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle className="w-2.5 h-2.5 text-emerald-600" />
                  <span>Rastreabilidade total</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sidebar Mobile */}
      <div className={`lg:hidden fixed top-0 left-0 bottom-0 w-72 z-50 transform transition-transform duration-300 ${
        sidebarMobile ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="h-full glass-card-elevated-refined border-r border-gray-200/50">
          <div className="p-4 h-full overflow-y-auto custom-scrollbar pt-16">
            <button
              onClick={() => setSidebarMobile(false)}
              className="absolute top-4 right-4 w-7 h-7 bg-red-100/80 hover:bg-red-200/90 text-red-600 hover:text-red-700 rounded-lg border border-red-200/50 transition-all duration-200 flex items-center justify-center shadow-sm hover:shadow-md"
            >
              <X className="w-3.5 h-3.5" />
            </button>

            <div className="space-y-2 mb-5">
              <button
                onClick={() => {
                  navigate('/dashboard');
                  setSidebarMobile(false);
                }}
                className="sidebar-button-refined group"
              >
                <div className="sidebar-icon-refined">
                  <ArrowLeft className="w-3.5 h-3.5" />
                </div>
                <span className="sf-pro-text text-xs">Voltar ao Dispensador</span>
              </button>

              <button
                onClick={() => setSidebarMobile(false)}
                className="sidebar-button-refined sidebar-button-active-refined"
              >
                <div className="sidebar-icon-refined sidebar-icon-active-refined">
                  <Clock className="w-3.5 h-3.5" />
                </div>
                <span className="sf-pro-text text-xs">Histórico</span>
              </button>
            </div>

            <div className="glass-info-card-refined rounded-xl p-3.5 border border-gray-200/50">
              <h3 className="sf-pro-display font-bold text-gray-900 text-xs flex items-center gap-2 mb-2">
                <div className="icon-container-refined p-1 rounded-lg bg-emerald-100/80 border-emerald-200/50">
                  <Info className="w-3 h-3 text-emerald-600" />
                </div>
                Sobre o Histórico
              </h3>
              <p className="sf-pro-text text-[10px] text-gray-600 leading-relaxed">
                Visualize todas as dispensações realizadas.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Conteúdo Principal */}
      <div className="lg:pl-72 relative z-10">
        <div className="pt-20 sm:pt-24 pb-8 px-3 sm:px-6">
          <div className="max-w-7xl mx-auto">
            
            {/* Título */}
            <div className="text-center mb-5">
              <h1 className="text-xl sm:text-2xl sf-pro-display font-semibold text-gray-900 tracking-tight mb-1">
                Histórico de Dispensações
              </h1>
              <p className="sf-pro-text text-gray-500 font-medium text-xs">
                Todas as suas dispensações em um só lugar
              </p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 mb-5">
              <StatCard
                icon={FileText}
                label="Total de Dispensações"
                value={dispensacoes.length}
                color="emerald"
              />
              <StatCard
                icon={Calendar}
                label="Após Filtros"
                value={filteredDispensations.length}
                color="blue"
              />
              <StatCard
                icon={TrendingUp}
                label="Este Mês"
                value={statsThisMonth}
                color="purple"
              />
            </div>

            {/* Filtros */}
            <GlassCard elevated className="p-4 sm:p-5 shadow-xl mb-5">
              <div className="flex items-center justify-between mb-3.5">
                <div className="flex items-center space-x-2.5">
                  <div className="icon-container-refined p-2 rounded-xl bg-emerald-100/80 border-emerald-200/50">
                    <Search className="w-4 h-4 text-emerald-600" />
                  </div>
                  <h2 className="card-title-refined">
                    Buscar Dispensações
                  </h2>
                </div>
                
                <button
                  onClick={() => setFiltrosAbertos(!filtrosAbertos)}
                  className="sm:hidden button-glass-refined"
                >
                  <Filter className="w-3.5 h-3.5" />
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${filtrosAbertos ? 'rotate-180' : ''}`} />
                </button>
              </div>

              <div className={`${filtrosAbertos ? 'block' : 'hidden sm:block'}`}>
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 rounded-xl blur-xl opacity-0 group-focus-within:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 transition-colors group-focus-within:text-emerald-500" />
                    <input
                      type="text"
                      placeholder="Buscar por paciente ou ID da prescrição..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="input-glass-refined w-full pl-10 text-xs"
                      maxLength="100"
                    />
                  </div>
                </div>
              </div>
            </GlassCard>

            {/* Conteúdo - Loading, Error ou Lista */}
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <GlassCard elevated className="p-7 shadow-xl">
                  <div className="loading-container-refined">
                    <div className="loading-animation-refined">
                      <div className="loading-glow-refined"></div>
                      <div className="loading-icon-refined">
                        <Loader2 className="w-6 h-6 text-white animate-spin" />
                      </div>
                    </div>
                    <div className="text-center">
                      <h3 className="sf-pro-display text-slate-900 font-bold text-sm">Carregando...</h3>
                      <p className="sf-pro-text text-slate-500 text-xs mt-1">Buscando histórico</p>
                    </div>
                  </div>
                </GlassCard>
              </div>
            ) : error ? (
              <ErrorState error={error} onRetry={() => loadHistorico(true)} />
            ) : filteredDispensations.length === 0 ? (
              <EmptyState />
            ) : (
              <>
                {/* Lista de Dispensações Agrupadas */}
                <div className="space-y-5">
                  {grupos.map(([nomeGrupo, dispensacoesGrupo]) => (
                    <div key={nomeGrupo}>
                      <GlassCard elevated className="shadow-xl overflow-hidden">
                        <div className="px-4 sm:px-5 py-3.5 bg-gradient-to-r from-emerald-50/80 to-teal-50/60 border-b border-emerald-200/30">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2.5">
                              <div className="icon-container-refined p-1.5 rounded-xl bg-emerald-100/80 border-emerald-200/50">
                                <Calendar className="w-3.5 h-3.5 text-emerald-600" />
                              </div>
                              <h3 className="card-title-refined">
                                {nomeGrupo}
                              </h3>
                            </div>
                            <span className="glass-badge-refined text-[10px] bg-emerald-100/80 text-emerald-700 border-emerald-200/50">
                              {dispensacoesGrupo.length} {dispensacoesGrupo.length !== 1 ? 'dispensações' : 'dispensação'}
                            </span>
                          </div>
                        </div>

                        <div className="divide-y divide-gray-200/50">
                          {dispensacoesGrupo.map((dispensation) => (
                            <div key={dispensation.id || dispensation.prescriptionId} className="p-3.5 sm:p-4">
                              <DispensationCard 
                                dispensation={dispensation}
                                onClick={() => abrirModal(dispensation)}
                              />
                            </div>
                          ))}
                        </div>
                      </GlassCard>
                    </div>
                  ))}
                </div>

                {/* Load More Button */}
                {hasMore && (
                  <div className="mt-6 text-center">
                    <button
                      onClick={loadMore}
                      disabled={loadingMore}
                      className="button-glass-refined disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loadingMore ? (
                        <div className="flex items-center space-x-2">
                          <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                          <span className="sf-pro-text text-xs">Carregando...</span>
                        </div>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4" />
                          <span className="sf-pro-text text-xs">Carregar mais dispensações</span>
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Fim da Lista */}
                {!hasMore && filteredDispensations.length > 0 && (
                  <div className="text-center py-5">
                    <div className="inline-flex items-center space-x-2 glass-badge-refined text-[10px] bg-emerald-100/80 text-emerald-700 border-emerald-200/50">
                      <CheckCircle className="w-3 h-3 text-emerald-600" />
                      <span className="sf-pro-text">Todas as dispensações foram carregadas</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Visualização */}
      <ModalVisualizarDispensacao
        visivel={modalAberto}
        dispensacao={dispensacaoSelecionada}
        onFechar={fecharModal}
        onDownloadXml={handleDownloadXml}
      />

      {/* Footer Premium */}
      <footer className="footer-refined lg:ml-72">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
            <img 
              src="/logo.PNG"
              alt="Scriptus" 
              className="h-3.5 w-auto opacity-60"
            />
            <span className="text-[10px] sf-pro-text text-slate-500">
              Scriptus Tecnologia em Saúde LTDA © 62.420.408/0001-70
            </span>
          </div>
        </div>
      </footer>

      {/* Estilos CSS Premium - Padronizado com Assinatura */}
      <style>{`
        /* ===== FONTES - PADRÃO ASSINATURA ===== */
        .sf-pro-display {
          font-family: -apple-system, "SF Pro Display", BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        .sf-pro-text {
          font-family: -apple-system, "SF Pro Text", BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        /* ===== BACKGROUND - PADRÃO ASSINATURA ===== */
        .bg-gradient-refined {
          background: linear-gradient(135deg, 
            #f8fafc 0%, 
            #ffffff 25%, 
            #f1f5f9 50%, 
            #ffffff 75%, 
            #ecfdf5 100%);
        }

        .background-pattern-refined {
          position: absolute;
          inset: 0;
          background-image: 
            radial-gradient(circle at 25% 25%, rgba(16, 185, 129, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 75% 75%, rgba(20, 184, 166, 0.06) 0%, transparent 50%),
            radial-gradient(circle at 50% 10%, rgba(99, 102, 241, 0.04) 0%, transparent 60%),
            radial-gradient(circle at 10% 90%, rgba(168, 85, 247, 0.03) 0%, transparent 50%);
          opacity: 0.7;
        }

        .background-glow-1 {
          position: absolute;
          top: 10%;
          left: 15%;
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(16, 185, 129, 0.08) 0%, transparent 70%);
          border-radius: 50%;
          filter: blur(40px);
        }

        .background-glow-2 {
          position: absolute;
          bottom: 15%;
          right: 20%;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(20, 184, 166, 0.06) 0%, transparent 70%);
          border-radius: 50%;
          filter: blur(35px);
        }

        .background-glow-3 {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(99, 102, 241, 0.02) 0%, transparent 70%);
          border-radius: 50%;
          filter: blur(60px);
        }

        /* ===== GLASS CARDS - PADRÃO ASSINATURA ===== */
        .glass-card-refined {
          background: rgba(255, 255, 255, 0.80);
          backdrop-filter: blur(24px) saturate(180%);
          -webkit-backdrop-filter: blur(24px) saturate(180%);
          border: 1px solid rgba(255, 255, 255, 0.5);
          box-shadow: 
            0 8px 32px rgba(0, 0, 0, 0.06),
            0 4px 16px rgba(0, 0, 0, 0.04),
            inset 0 1px 0 rgba(255, 255, 255, 0.9),
            inset 0 -1px 0 rgba(255, 255, 255, 0.5);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .glass-card-elevated-refined {
          background: rgba(255, 255, 255, 0.90);
          backdrop-filter: blur(30px) saturate(200%);
          -webkit-backdrop-filter: blur(30px) saturate(200%);
          border: 1px solid rgba(255, 255, 255, 0.6);
          box-shadow: 
            0 25px 50px rgba(0, 0, 0, 0.08),
            0 12px 24px rgba(0, 0, 0, 0.06),
            0 6px 12px rgba(0, 0, 0, 0.04),
            inset 0 2px 0 rgba(255, 255, 255, 0.95),
            inset 0 -1px 0 rgba(255, 255, 255, 0.6);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        /* ===== HEADER - PADRÃO ASSINATURA ===== */
        .header-refined {
          background: rgba(255, 255, 255, 0.75);
          backdrop-filter: blur(24px) saturate(180%);
          -webkit-backdrop-filter: blur(24px) saturate(180%);
          border-bottom: 1px solid rgba(255, 255, 255, 0.4);
        }

        .header-scrolled-refined {
          background: rgba(255, 255, 255, 0.90);
          backdrop-filter: blur(30px) saturate(200%);
          -webkit-backdrop-filter: blur(30px) saturate(200%);
          border-bottom: 1px solid rgba(255, 255, 255, 0.6);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
        }

        .logo-link-refined {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .logo-link-refined:hover {
          opacity: 0.8;
          transform: scale(1.05);
        }

        /* ===== CARD TITLE - PADRÃO ASSINATURA ===== */
        .card-title-refined {
          font-size: 0.875rem;
          font-weight: 700;
          color: #1e293b;
          letter-spacing: -0.025em;
          line-height: 1.3;
        }

        /* ===== ICON CONTAINER ===== */
        .icon-container-refined {
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.6));
          backdrop-filter: blur(16px);
          border: 1px solid rgba(255, 255, 255, 0.5);
          box-shadow: 
            0 4px 12px rgba(0, 0, 0, 0.05),
            0 1px 3px rgba(0, 0, 0, 0.03),
            inset 0 1px 0 rgba(255, 255, 255, 0.8);
          transition: all 0.2s ease;
        }

        /* ===== INFO CARDS ===== */
        .glass-info-card-refined {
          background: rgba(255, 255, 255, 0.6);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid rgba(255, 255, 255, 0.4);
          box-shadow: 
            0 4px 16px rgba(0, 0, 0, 0.04),
            inset 0 1px 0 rgba(255, 255, 255, 0.7);
          transition: all 0.2s ease;
        }

        .glass-info-card-refined:hover {
          background: rgba(255, 255, 255, 0.8);
          transform: translateY(-1px);
          box-shadow: 
            0 6px 20px rgba(0, 0, 0, 0.06),
            inset 0 1px 0 rgba(255, 255, 255, 0.8);
        }

        /* ===== BADGES ===== */
        .glass-badge-refined {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          background: rgba(243, 244, 246, 0.9);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border-radius: 8px;
          border: 1px solid rgba(255, 255, 255, 0.4);
          box-shadow: 
            0 2px 6px rgba(0, 0, 0, 0.05),
            inset 0 1px 0 rgba(255, 255, 255, 0.5);
        }

        /* ===== INPUT - PADRÃO ASSINATURA ===== */
        .input-glass-refined {
          padding: 12px 16px;
          background: rgba(255, 255, 255, 0.8);
          border: 1.5px solid rgba(255, 255, 255, 0.5);
          border-radius: 12px;
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          transition: all 0.2s ease;
          font-family: inherit;
          color: #1f2937;
          box-shadow: 
            0 2px 8px rgba(0, 0, 0, 0.04),
            0 1px 2px rgba(0, 0, 0, 0.02),
            inset 0 1px 0 rgba(255, 255, 255, 0.8),
            inset 0 -1px 0 rgba(255, 255, 255, 0.3);
        }

        .input-glass-refined::placeholder {
          color: #9ca3af;
          font-weight: 400;
        }

        .input-glass-refined:focus {
          outline: none;
          background: rgba(255, 255, 255, 0.95);
          border-color: rgba(16, 185, 129, 0.5);
          box-shadow: 
            0 0 0 4px rgba(16, 185, 129, 0.1),
            0 4px 16px rgba(16, 185, 129, 0.1),
            0 2px 8px rgba(0, 0, 0, 0.06),
            inset 0 1px 0 rgba(255, 255, 255, 0.9);
          transform: translateY(-1px);
        }

        /* ===== BUTTONS - PADRÃO ASSINATURA ===== */
        .button-glass-refined {
          padding: 10px 16px;
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1.5px solid rgba(255, 255, 255, 0.6);
          border-radius: 12px;
          font-weight: 600;
          color: #475569;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          box-shadow: 
            0 6px 16px rgba(0, 0, 0, 0.04),
            inset 0 1px 0 rgba(255, 255, 255, 0.8);
          cursor: pointer;
        }

        .button-glass-refined:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.95);
          color: #1e293b;
          transform: translateY(-1px);
          box-shadow: 
            0 10px 24px rgba(0, 0, 0, 0.08),
            inset 0 1px 0 rgba(255, 255, 255, 0.9);
        }

        .button-primary-refined {
          padding: 12px 18px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border: none;
          border-radius: 12px;
          color: white;
          font-weight: 700;
          font-size: 0.75rem;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          box-shadow: 
            0 10px 32px rgba(16, 185, 129, 0.25),
            0 6px 16px rgba(16, 185, 129, 0.15),
            inset 0 2px 0 rgba(255, 255, 255, 0.25);
          cursor: pointer;
        }

        .button-primary-refined:hover:not(:disabled) {
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
          transform: translateY(-2px) scale(1.02);
          box-shadow: 
            0 16px 40px rgba(16, 185, 129, 0.3),
            0 8px 20px rgba(16, 185, 129, 0.2);
        }

        /* ===== SIDEBAR BUTTONS ===== */
        .sidebar-button-refined {
          width: 100%;
          text-align: left;
          padding: 10px 14px;
          border-radius: 12px;
          font-weight: 600;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          align-items: center;
          gap: 10px;
          color: #64748b;
          background: rgba(255, 255, 255, 0.6);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          box-shadow: 
            0 1px 3px rgba(0, 0, 0, 0.05),
            inset 0 1px 0 rgba(255, 255, 255, 0.6);
        }

        .sidebar-button-refined:hover {
          background: rgba(255, 255, 255, 0.8);
          color: #10b981;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .sidebar-button-active-refined {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          box-shadow: 
            0 8px 24px rgba(16, 185, 129, 0.25),
            0 4px 12px rgba(16, 185, 129, 0.15);
          transform: scale(1.02);
        }

        .sidebar-icon-refined {
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          background: rgba(148, 163, 184, 0.15);
          transition: all 0.2s ease;
        }

        .sidebar-button-refined:hover .sidebar-icon-refined {
          background: rgba(16, 185, 129, 0.15);
        }

        .sidebar-icon-active-refined {
          background: rgba(255, 255, 255, 0.25);
        }

        /* ===== LOADING - PADRÃO ASSINATURA ===== */
        .loading-container-refined {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 16px;
          gap: 16px;
        }

        .loading-animation-refined {
          position: relative;
        }

        .loading-glow-refined {
          position: absolute;
          inset: -16px;
          background: rgba(16, 185, 129, 0.15);
          border-radius: 50%;
          filter: blur(20px);
        }

        .loading-icon-refined {
          position: relative;
          width: 56px;
          height: 56px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border-radius: 50%;
          box-shadow: 
            0 12px 32px rgba(16, 185, 129, 0.3),
            0 6px 16px rgba(16, 185, 129, 0.2);
        }

        /* ===== FOOTER - PADRÃO ASSINATURA ===== */
        .footer-refined {
          background: rgba(255, 255, 255, 0.75);
          backdrop-filter: blur(20px);
          border-top: 1px solid rgba(255, 255, 255, 0.5);
          padding: 16px 0;
          margin-top: 48px;
        }

        /* ===== SCROLLBAR ===== */
        .custom-scrollbar::-webkit-scrollbar {
          width: 5px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(241, 245, 249, 0.5);
          border-radius: 5px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(203, 213, 225, 0.8);
          border-radius: 5px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(148, 163, 184, 0.9);
        }

        /* ===== RESPONSIVE ===== */
        @media (max-width: 768px) {
          .glass-card-elevated-refined, 
          .glass-card-refined {
            backdrop-filter: blur(16px);
            -webkit-backdrop-filter: blur(16px);
          }
          
          .input-glass-refined {
            font-size: 16px;
            border-radius: 12px;
          }
        }

        /* ===== ACCESSIBILITY ===== */
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }

        @media (prefers-reduced-transparency: reduce) {
          .glass-card-elevated-refined,
          .glass-card-refined,
          .header-refined,
          .header-scrolled-refined {
            background: rgba(255, 255, 255, 0.98);
            backdrop-filter: none;
            -webkit-backdrop-filter: none;
          }
        }

        @media (prefers-contrast: high) {
          .glass-card-elevated-refined,
          .glass-card-refined {
            background: rgba(255, 255, 255, 0.95);
            border: 2px solid rgba(0, 0, 0, 0.2);
          }
        }

        @media (pointer: coarse) {
          button, a, [role="button"] {
            min-height: 44px;
          }
        }

        html {
          scroll-behavior: smooth;
        }
      `}</style>
    </div>
  );
}